import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {API_BASED_URL} from '../config.js'; 

const OperatorChangePassword = () => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);

    const handleChangePassword = async () => {
        setIsLoading(true);
        setMessage({ text: '', type: '' });
        const authToken = localStorage.getItem('userToken');
        const userEmail = localStorage.getItem('userEmail');

        try {
            const response = await axios.post(`${API_BASED_URL}api/op-change-password`, {
                oldPassword,
                newPassword,
                userEmail
            }, {
                headers: {
                    'Authorization': `${authToken}`
                }
            });
            if (response.data.success) {
                setMessage({ text: 'Password changed successfully', type: 'success' });
            } else {
                setMessage({ text: response.data.message || 'An error occurred', type: 'error' });
            }
        } catch (error) {
            console.error('Error changing password', error);
            const errorMessage = error.response?.data?.message || 'An error occurred while changing the password';
            setMessage({ text: errorMessage, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-xl"
        >
            <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Change Password</h2>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="oldPassword">
                        Old Password
                    </label>
                    <div className="relative">
                        <input
                            id="oldPassword"
                            type={showOld ? 'text' : 'password'}
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 pr-10"
                        />
                        <button type="button" aria-label={showOld ? 'Hide password' : 'Show password'} onClick={() => setShowOld(v=>!v)} className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex w-7 h-7 items-center justify-center p-0.5 rounded-md text-gray-500 hover:text-gray-700 bg-transparent hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500">{showOld ? <EyeOff size={18}/> : <Eye size={18}/>}</button>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="newPassword">
                        New Password
                    </label>
                    <div className="relative">
                        <input
                            id="newPassword"
                            type={showNew ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 pr-10"
                        />
                        <button type="button" aria-label={showNew ? 'Hide password' : 'Show password'} onClick={() => setShowNew(v=>!v)} className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex w-7 h-7 items-center justify-center p-0.5 rounded-md text-gray-500 hover:text-gray-700 bg-transparent hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500">{showNew ? <EyeOff size={18}/> : <Eye size={18}/>}</button>
                    </div>
                </div>
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleChangePassword}
                    disabled={isLoading}
                    className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {isLoading ? 'Changing Password...' : 'Change Password'}
                </motion.button>
            </div>
            {message.text && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mt-4 p-2 rounded ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                >
                    {message.text}
                </motion.div>
            )}
        </motion.div>
    );
};

export default OperatorChangePassword;
